import { customElement, query, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement } from 'lit'
import { CSEButtonInput, CSEDropdown } from '@cloudsuite/mosaic-elements'

import { STORE_LOCATOR_STORE, StoreLocatorStore } from 'components/discovery/store-locator/store-locator-store.js'
import { t, Translator } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './store-search-field.style.js'

import {
  Autocomplete,
  Prediction,
  AutocompleteModel,
} from 'components/discovery/store-locator/typing/google-autocomplete.js'
import { CSMapCoordinates } from 'components/discovery/store-locator/typing/internal.js'
import { classMap } from 'lit/directives/class-map.js'
import { Debouncer, DEBOUNCER } from '@cloudsuite/mosaic-shared/scripts/debounce.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


initializeIconsByRole({
  'csc-stores-search-field.search': { name: 'location-crosshairs', style: 'light' },
  'csc-stores-search-field.select-option': { name: 'angle-right', style: 'light' }
})

@customElement('csc-store-search-field')
export class CSCStoreSearchField extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(STORE_LOCATOR_STORE)
  storeLocatorStore!: StoreLocatorStore

  @inject(DEBOUNCER)
  debounce!: Debouncer

  @state()
  searchResults: Autocomplete = new AutocompleteModel()

  @state()
  mapCoordination: CSMapCoordinates | null = null

  @query('cse-button-input')
  input!: CSEButtonInput

  @query('cse-dropdown')
  dropdown!: CSEDropdown

  constructor() {
    super()

    createEffect(() => {
      this.searchResults = this.storeLocatorStore.autocompleteResults()
      this.mapCoordination = this.storeLocatorStore.mapCoordinates()
    })
  }

  searchInputChanged = this.debounce.debounce(() => {
    if (this.input.value !== '') {
      this.storeLocatorStore.autocompletePlaces(this.input.value)
      this.dropdown.active = true
    } else {
      this.storeLocatorStore.clearAutocompletion()
    }
  }, 250)

  useCurrentLocationClicked = () => {
    this.storeLocatorStore.useCurrentLocation()
  }

  targetIconClicked = (e: Event) => {
    e.stopPropagation()
    this.useCurrentLocationClicked()
  }

  autocompleteOptionClicked = (place: any) => {
    this.input.value = place.description
    this.storeLocatorStore.geocodeLocate(this.input.value)
  }

  buttonInputSubmitted = this.debounce.debounce(() => {
    this.storeLocatorStore.geocodeLocate(this.input.value)
  }, 250)

  clearInputClicked = () => {
    this.storeLocatorStore.clearAutocompletion()
  }

  shouldRenderCurrentLocation = () => {
    if (this.searchResults.predictions.length > 0) {
      return false
    } else {
      return this.input == null || this.input.value === ''
    }
  }

  calculateSearchPlaceholder = () => {
    return this.mapCoordination?.type === 'current_location'
      ? t('use_current_location', { ns: 'csc-store-locator', default: 'Current location' })
      : t('search_field_placeholder', { ns: 'csc-store-locator', default: 'Search address, name or city' })
  }

  renderButtonInput = () => html`
    <cse-form-field  slot="anchor">
      <cse-label slot="label">
        ${t('search_field_label', { ns: 'csc-store-locator', default: 'Find a store' })}
      </cse-label>

      <cse-button-input
        button-variant="primary"
        placeholder="${this.calculateSearchPlaceholder()}"
        @keyup="${this.searchInputChanged}"
        @cse-button-input-clear="${this.clearInputClicked}"
        @cse-button-input-submit="${this.buttonInputSubmitted}"
      >
        <cse-icon
          slot="suffix-icon"
          icon-role="csc-stores-search-field.search"
          class="${classMap({
            'icon-selected': this.mapCoordination?.type === 'current_location'
          })}"
          @click="${this.targetIconClicked}"
        ></cse-icon>

        <span slot="button-text">
          ${t('search_field_button_text', { ns: 'csc-store-locator', default: 'Search' })}
        </span>
      </cse-button-input>
    </cse-form-field>
  `

  renderCurrentLocation = () => html`
    <cse-option
      value="current-location"
      @click="${this.useCurrentLocationClicked}"
      variant="search"
    >
      ${t('use_current_location', { ns: 'csc-store-locator', default: 'Current location' })}
      <cse-icon
        slot="suffix"
        id="current-location-select-icon"
        icon-role="csc-stores-search-field.select-option"
      ></cse-icon>
    </cse-option>
  `

  renderAutoCompleteOption = (place: Prediction) => html`
    <cse-option
      value="${place.description}"
      @click="${() => this.autocompleteOptionClicked(place)}"
      variant="search"
    >
      ${place.description}
      <cse-icon
        slot="suffix"
        id="autocomplete-option-select-icon"
        icon-role="csc-stores-search-field.select-option"
      ></cse-icon>
    </cse-option>
  `

  renderAutocompleteResults = () => {
    if (this.searchResults.predictions.length > 0) {
      return this.searchResults.predictions.map(
        (place: Prediction) => this.renderAutoCompleteOption(place)
      )
    } else {
      return html`
        <cse-text>
          ${new Translator('no_autocomplete_results_found', { ns: 'csc-store-locator', default: 'No results found' }).translate()}
        </cse-text>
      `
    }
  }

  renderSearchOptions = () => html`
    <div class="search-options">
      ${this.shouldRenderCurrentLocation()
        ? this.renderCurrentLocation()
        : this.renderAutocompleteResults()
      }
    </div>
  `

  override render() {
    return html`
      <cse-dropdown menu-width="anchor" full-width>
        ${this.renderButtonInput()}
        ${this.renderSearchOptions()}
      </cse-dropdown>
    `
  }
}
